import React, { useCallback, useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  Alert, Badge, Button, Card, Checkbox, Col, Descriptions, Empty, Input,
  InputNumber, List, Modal, Result, Row, Space, Spin, Tag, Typography,
  message,
} from 'antd';
import ResponsiveTable from '../../components/common/ResponsiveTable';
import {
  CheckCircleOutlined, CloseCircleOutlined, DownloadOutlined, EyeOutlined,
  IdcardOutlined, TrophyOutlined,
} from '@ant-design/icons';
import dayjs from 'dayjs';
import {
  createParentTraveler, deleteParentTraveler, getParentCompetition,
  getParentCompetitions, sendFeeConsent, sendParticipationConsent,
} from '../../api/competitions';
import apiErrorMessage from '../../utils/apiError';
import { sanitizeHtml } from '../../utils/sanitizeHtml';
import { tahsilatZamani } from '../../utils/tahsilatZamani';

const { Title, Text, Paragraph } = Typography;

const SYMBOL = { TRY: '₺', EUR: '€', USD: '$' };
const money = (a, c) =>
  `${Number(a || 0).toLocaleString('tr-TR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} ${SYMBOL[c] || c}`;

const ACTION_LABEL = {
  participation_consent: { text: 'Katılım onayınız bekleniyor', color: 'processing' },
  fee_consent:           { text: 'Ücret onayınız bekleniyor',   color: 'warning' },
  passport_info:         { text: 'Pasaport bilgisi girmelisiniz', color: 'error' },
};

const STATUS_LABEL = {
  candidate:              'Davet edildiniz',
  participation_approved: 'Katılımı onayladınız',
  participation_declined: 'Katılmıyorsunuz',
  fee_approved:           'Ücreti onayladınız',
  fee_declined:           'Vazgeçtiniz',
  confirmed:              'Katılımınız kesinleşti',
  cancelled:              'İptal edildi',
};

const ParentCompetitions = () => {
  const [params, setParams] = useSearchParams();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedId, setSelectedId] = useState(params.get('participant'));

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await getParentCompetitions();
      setRows(data.data || []);
    } catch (e) {
      message.error(apiErrorMessage(e, 'Yarışmalar yüklenemedi.'));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  // Bildirime tiklayinca ?participant=123 ile gelinir ve dogrudan o yarismanin
  // detayi acilir. Onaylarin KVKK gibi zorunlu acilir pencere olmamasinin
  // sebebi bu: veli bildirimden gelip onayi burada veriyor.
  const closeDetail = () => {
    setSelectedId(null);
    if (params.get('participant')) {
      params.delete('participant');
      setParams(params, { replace: true });
    }
  };

  if (loading) return <div style={{ textAlign: 'center', padding: 40 }}><Spin /></div>;

  return (
    <div>
      <Title level={3}><TrophyOutlined /> Yarışmalar</Title>

      {rows.length === 0 ? (
        <Empty description="Şu anda katılabileceğiniz bir yarışma yok." />
      ) : (
        <Row gutter={[16, 16]}>
          {rows.map((r) => {
            const action = ACTION_LABEL[r.action_needed];
            return (
              <Col key={r.id} xs={24} md={12} lg={8}>
                <Badge.Ribbon
                  text={action ? action.text : STATUS_LABEL[r.status]}
                  color={action ? action.color : 'default'}
                >
                  <Card
                    hoverable
                    onClick={() => setSelectedId(String(r.id))}
                    title={r.stage?.name}
                  >
                    <Space orientation="vertical" size={4} style={{ width: '100%' }}>
                      <Text strong>{r.student?.name} {r.student?.surname}</Text>
                      <Text type="secondary">
                        {r.stage?.competition?.name} · {r.stage?.competition?.year}
                      </Text>
                      {r.stage?.start_date && (
                        <Text type="secondary">
                          {dayjs(r.stage.start_date).format('DD.MM.YYYY')}
                          {r.stage.location ? ` · ${r.stage.location}` : ''}
                          {r.stage.is_abroad && <Tag color="blue" style={{ marginLeft: 6 }}>Yurt dışı</Tag>}
                        </Text>
                      )}
                    </Space>
                  </Card>
                </Badge.Ribbon>
              </Col>
            );
          })}
        </Row>
      )}

      <CompetitionDetailModal
        participantId={selectedId}
        onClose={closeDetail}
        onChanged={load}
      />
    </div>
  );
};

const CompetitionDetailModal = ({ participantId, onClose, onChanged }) => {
  const [detail, setDetail] = useState(null);
  const [loading, setLoading] = useState(false);
  const [attendeeCount, setAttendeeCount] = useState(1);
  const [optionalIds, setOptionalIds] = useState([]);
  const [saving, setSaving] = useState(false);
  const [notFound, setNotFound] = useState(false);

  const load = useCallback(async (count) => {
    if (!participantId) return;
    setLoading(true);
    setNotFound(false);
    try {
      const { data } = await getParentCompetition(participantId, { attendee_count: count });
      setDetail(data);
      // DİKKAT: kullanıcı kişi sayısını değiştirdiğinde load(count) çağrılıyor.
      // Sunucudan dönen kayıtlı değeri koşulsuz yazarsak seçim anında geri
      // alınır: tablo 5 kişilik tutarı gösterirken state 1'e döner ve onay
      // 1 kişiyle kaydedilir. Yalnızca ilk açılışta (count verilmemişken) yaz.
      if (count === undefined) {
        setAttendeeCount(data.participant?.attendee_count || 1);
      }
    } catch (e) {
      if (e?.response?.status === 403 || e?.response?.status === 404) setNotFound(true);
      else message.error(apiErrorMessage(e, 'Yarışma bilgisi yüklenemedi.'));
    } finally {
      setLoading(false);
    }
  }, [participantId]);

  useEffect(() => { if (participantId) load(); }, [participantId, load]);

  const participant = detail?.participant;
  const stage = detail?.stage;

  const decide = async (fn, payload, successCb) => {
    setSaving(true);
    try {
      const { data } = await fn(participantId, payload);
      message.success(data.message);
      await load(attendeeCount);
      onChanged();
      successCb?.();
    } catch (e) {
      message.warning(apiErrorMessage(e));
    } finally {
      setSaving(false);
    }
  };

  // Zorunlu kalemler + velinin işaretlediği isteğe bağlı kalemler.
  // Sunucudan gelen fee_preview.totals TÜM kalemleri kapsıyor; veli yalnızca
  // seçtiklerini ödeyeceği için toplam burada yeniden hesaplanıyor.
  const previewLines = (detail?.fee_preview?.lines || []).filter(
    (l) => !l.is_optional || optionalIds.includes(l.fee_item_id)
  );
  const selectedTotals = previewLines.reduce((acc, l) => {
    acc[l.currency] = Math.round(((acc[l.currency] || 0) + l.total) * 100) / 100;
    return acc;
  }, {});

  return (
    <Modal
      open={!!participantId}
      onCancel={onClose}
      footer={null}
      width={780}
      title={stage ? `${stage.name} — ${participant?.student?.name} ${participant?.student?.surname}` : 'Yarışma'}
      destroyOnHidden
    >
      {notFound ? (
        <Result status="403" title="Bu kayda erişim yetkiniz yok." />
      ) : loading || !detail ? (
        <div style={{ textAlign: 'center', padding: 40 }}><Spin /></div>
      ) : (
        <Space orientation="vertical" size={16} style={{ width: '100%' }}>
          <Descriptions bordered size="small" column={{ xs: 1, sm: 2 }}>
            <Descriptions.Item label="Yarışma">{detail.competition?.name}</Descriptions.Item>
            <Descriptions.Item label="Etap">{stage?.name}</Descriptions.Item>
            <Descriptions.Item label="Yer">
              {[stage?.location, stage?.country].filter(Boolean).join(' / ') || '—'}
            </Descriptions.Item>
            <Descriptions.Item label="Tarih">
              {stage?.start_date ? dayjs(stage.start_date).format('DD.MM.YYYY') : '—'}
              {stage?.end_date ? ` – ${dayjs(stage.end_date).format('DD.MM.YYYY')}` : ''}
            </Descriptions.Item>
            <Descriptions.Item label="Durumunuz" span={2}>
              <Tag>{STATUS_LABEL[participant?.status] || participant?.status}</Tag>
            </Descriptions.Item>
          </Descriptions>

          {stage?.info_text && (
            <Card size="small" title="Yarışma Hakkında">
              {/* Zengin metin yoneticiden geliyor - sanitize sart (stored XSS) */}
              <div dangerouslySetInnerHTML={{ __html: sanitizeHtml(stage.info_text) }} />
            </Card>
          )}

          {detail.documents?.length > 0 && (
            <Card size="small" title="Belgeler">
              <List
                size="small"
                dataSource={detail.documents}
                renderItem={(d) => (
                  <List.Item
                    actions={[
                      <Button size="small" icon={<EyeOutlined />} onClick={() => window.open(d.stream_url, '_blank')}>
                        Görüntüle
                      </Button>,
                      <Button size="small" icon={<DownloadOutlined />} onClick={() => window.open(d.download_url, '_blank')}>
                        İndir
                      </Button>,
                    ]}
                  >
                    <List.Item.Meta
                      title={d.title || d.original_name}
                      description={d.type?.label}
                    />
                  </List.Item>
                )}
              />
            </Card>
          )}

          {/* 1. ASAMA - katilim onayi */}
          {participant?.participation_consent_status === 'pending' && (
            <Card size="small" title="Katılım Onayı">
              <Paragraph type="secondary">
                Yukarıdaki bilgileri inceledikten sonra çocuğunuzun bu yarışmaya
                katılmasını onaylayabilirsiniz. Ücret bilgisi daha sonra ayrıca
                onayınıza sunulacaktır.
              </Paragraph>
              <Space>
                <Button
                  type="primary"
                  icon={<CheckCircleOutlined />}
                  loading={saving}
                  onClick={() => decide(sendParticipationConsent, { decision: 'approve' })}
                >
                  Katılmak İstiyorum
                </Button>
                <Button
                  danger
                  icon={<CloseCircleOutlined />}
                  loading={saving}
                  onClick={() => Modal.confirm({
                    title: 'Katılmıyor musunuz?',
                    content: 'Tercihiniz kaydedilecek. Fikrinizi değiştirirseniz kurumla iletişime geçebilirsiniz.',
                    okText: 'Katılmıyorum',
                    cancelText: 'Vazgeç',
                    onOk: () => decide(sendParticipationConsent, { decision: 'decline' }),
                  })}
                >
                  Katılmıyorum
                </Button>
              </Space>
            </Card>
          )}

          {/* 2. ASAMA - ucret onayi */}
          {participant?.participation_consent_status === 'approved'
            && participant?.fee_consent_status === 'pending'
            && (detail.fee_preview?.lines?.length > 0) && (
            <Card size="small" title="Ücret Onayı">
              <Paragraph type="secondary">
                Kaç kişi katılacaksınız? Ücret kişi başı hesaplanır — öğrenci
                dışında sizin de katılmanız durumunda kişi sayısını buna göre seçin.
              </Paragraph>
              <Space wrap style={{ marginBottom: 12 }}>
                <Text>Katılacak kişi sayısı:</Text>
                <InputNumber
                  min={1}
                  max={20}
                  value={attendeeCount}
                  onChange={(v) => { if (v) { setAttendeeCount(v); load(v); } }}
                />
              </Space>

              {detail.fee_preview.lines.some((l) => l.is_optional) && (
                <div style={{ marginBottom: 12 }}>
                  <Text strong>Ek hizmetler</Text>
                  <div style={{ marginTop: 6 }}>
                    {detail.fee_preview.lines.filter((l) => l.is_optional).map((l) => (
                      <Checkbox
                        key={l.fee_item_id}
                        checked={optionalIds.includes(l.fee_item_id)}
                        onChange={(e) => setOptionalIds((prev) =>
                          e.target.checked ? [...prev, l.fee_item_id] : prev.filter((x) => x !== l.fee_item_id))}
                      >
                        {l.label} — {money(l.total, l.currency)}
                      </Checkbox>
                    ))}
                  </div>
                </div>
              )}

              <ResponsiveTable
                size="small"
                scroll={{ x: 'max-content' }}
                rowKey="fee_item_id"
                dataSource={previewLines}
                pagination={false}
                columns={[
                  { title: 'Kalem', dataIndex: 'label' },
                  {
                    title: 'Hesap',
                    render: (_, r) => (r.basis === 'per_person'
                      ? `${money(r.unit_amount, r.currency)} × ${r.quantity} kişi`
                      : 'Sabit'),
                  },
                  { title: 'Tutar', dataIndex: 'total', width: 130, render: (v, r) => <b>{money(v, r.currency)}</b> },
                ]}
              />

              <Space wrap style={{ marginTop: 12 }}>
                {Object.entries(selectedTotals).map(([cur, val]) => (
                  <Tag key={cur} color="blue" style={{ fontSize: 14, padding: '4px 10px' }}>
                    Toplam: {money(val, cur)}
                  </Tag>
                ))}
              </Space>

              <div style={{ marginTop: 12 }}>
                <Space>
                  <Button
                    type="primary"
                    icon={<CheckCircleOutlined />}
                    loading={saving}
                    onClick={() => decide(sendFeeConsent, {
                      decision: 'approve',
                      attendee_count: attendeeCount,
                      optional_item_ids: optionalIds,
                    })}
                  >
                    Onaylıyorum
                  </Button>
                  <Button
                    danger
                    icon={<CloseCircleOutlined />}
                    loading={saving}
                    onClick={() => Modal.confirm({
                      title: 'Vazgeçiyor musunuz?',
                      content: 'Ücreti onaylamadığınız kaydedilecek.',
                      okText: 'Vazgeçiyorum',
                      cancelText: 'Devam et',
                      onOk: () => decide(sendFeeConsent, { decision: 'decline' }),
                    })}
                  >
                    Vazgeçiyorum
                  </Button>
                </Space>
              </div>
            </Card>
          )}

          {/* Onaylanmis ucret ve taksitler */}
          {participant?.fee_consent_status === 'approved' && (
            <Card size="small" title="Ödeme Planınız">
              <Space wrap style={{ marginBottom: 12 }}>
                {Object.entries(detail.totals || {}).map(([cur, val]) => (
                  <Tag key={cur} color="blue">Toplam: {money(val, cur)}</Tag>
                ))}
                {Object.entries(detail.paid || {}).map(([cur, val]) => (
                  <Tag key={`p-${cur}`} color="success">Ödenen: {money(val, cur)}</Tag>
                ))}
              </Space>
              <ResponsiveTable
                size="small"
                scroll={{ x: 'max-content' }}
                rowKey="id"
                dataSource={participant.installments || []}
                pagination={false}
                locale={{ emptyText: 'Ödeme planı henüz hazırlanmadı, hazır olduğunda bilgilendirileceksiniz.' }}
                columns={[
                  { title: 'Taksit', dataIndex: 'label', render: (v, r) => v || `${r.no}. Taksit` },
                  {
                    title: 'Vade',
                    dataIndex: 'due_date',
                    width: 120,
                    render: (v) => (v ? dayjs(v).format('DD.MM.YYYY') : '—'),
                  },
                  { title: 'Tutar', dataIndex: 'amount', width: 120, render: (v, r) => money(v, r.currency) },
                  {
                    title: 'Durum',
                    width: 110,
                    render: (_, r) => {
                      if (r.paid_at) return <Tag color="success">Ödendi</Tag>;
                      const overdue = r.due_date && dayjs(r.due_date).isBefore(dayjs(), 'day');
                      return <Tag color={overdue ? 'error' : 'default'}>{overdue ? 'Gecikti' : 'Bekliyor'}</Tag>;
                    },
                  },
                  /*
                    ÖDEME DÖKÜMÜ — ne zaman, ne kadar, kim tahsil etti.

                    Eren Bey bildirdi (22.10.2026): veli yalnızca "Ödendi"
                    görüyordu, tarihini bile göremiyordu. Normal ders
                    ödemelerinde bu bilgi veliye ZATEN gösteriliyor
                    (`ParentPayments` → "Tarih / Tutar / Yöntem / Alan");
                    yarışmada eksikti ve veli her soru için kuruma telefon
                    etmek zorunda kalıyordu.

                    ⚠ KOLON olarak eklendi, genişletilebilir satır olarak
                    DEĞİL: `ResponsiveTable` telefonda kart çiziyor ve
                    yalnızca `columns`/`render`'ı geçiriyor — `expandable`
                    mobilde sessizce yok sayılırdı. Veli ekranları mobil
                    öncelikli (CLAUDE.md), yani veliye hiç görünmeyen bir
                    çözüm olurdu.

                    Kısmi tahsilatta birden fazla satır olabiliyor; hepsi
                    yazılıyor çünkü "500 ödedim ama 300 görünüyor" sorusunun
                    cevabı tam olarak bu döküm.

                    Tahsilat NOTU burada YOK — sunucu veliye göndermiyor
                    (personelin kendi notu).
                  */
                  {
                    title: 'Ödeme',
                    key: 'odemeler',
                    render: (_, r) => {
                      const liste = r.payments || [];
                      // null döndürmek önemli: ResponsiveTable kart
                      // görünümünde boş alanı hiç çizmiyor, ödenmemiş
                      // taksitte gereksiz "Ödeme —" satırı çıkmıyor.
                      if (liste.length === 0) return null;
                      return (
                        <div className="text-xs leading-5">
                          {liste.map((p) => (
                            <div key={p.id}>
                              {tahsilatZamani(p.paid_at)} · {money(p.amount, r.currency)}
                              {p.receiver && (
                                <span className="text-gray-500">
                                  {' · '}{p.receiver.name} {p.receiver.surname}
                                </span>
                              )}
                            </div>
                          ))}
                        </div>
                      );
                    },
                  },
                ]}
              />
            </Card>
          )}

          {/* Pasaport - yalnizca yurt disi etapta */}
          {stage?.is_abroad && participant?.fee_consent_status === 'approved' && (
            <TravelersCard
              participant={participant}
              onChanged={() => { load(attendeeCount); onChanged(); }}
            />
          )}
        </Space>
      )}
    </Modal>
  );
};

const TravelersCard = ({ participant, onChanged }) => {
  const [form, setForm] = useState({ full_name: '', duty: 'student', birth_date: '', id_number: '', passport_no: '', passport_expiry: '' });
  const [saving, setSaving] = useState(false);

  const travelers = participant.travelers || [];
  const remaining = (participant.attendee_count || 1) - travelers.length;

  const add = async () => {
    if (!form.full_name.trim()) return message.warning('Ad soyad girin.');
    setSaving(true);
    try {
      await createParentTraveler(participant.id, {
        ...form,
        birth_date: form.birth_date || null,
        passport_expiry: form.passport_expiry || null,
      });
      message.success('Kişi eklendi.');
      setForm({ full_name: '', duty: 'student', birth_date: '', id_number: '', passport_no: '', passport_expiry: '' });
      onChanged();
    } catch (e) {
      message.warning(apiErrorMessage(e));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Card size="small" title={<><IdcardOutlined /> Katılacak Kişilerin Pasaport Bilgileri</>}>
      <Alert
        type="info"
        showIcon
        style={{ marginBottom: 12 }}
        message="Bu bilgiler vize ve organizasyon kaydı için gerekiyor"
        description={remaining > 0
          ? `Onayladığınız ${participant.attendee_count} kişiden ${travelers.length} tanesini girdiniz. ${remaining} kişi daha eklemelisiniz.`
          : 'Tüm kişilerin bilgilerini girdiniz. Teşekkürler.'}
      />

      <ResponsiveTable
        size="small"
        scroll={{ x: 'max-content' }}
        rowKey="id"
        dataSource={travelers}
        pagination={false}
        locale={{ emptyText: 'Henüz kişi eklenmedi.' }}
        columns={[
          { title: 'Ad Soyad', dataIndex: 'full_name' },
          {
            title: 'Görev',
            dataIndex: 'duty',
            width: 100,
            render: (v) => ({ student: 'Öğrenci', parent: 'Veli', teacher: 'Öğretmen', other: 'Diğer' }[v] || v),
          },
          { title: 'Pasaport No', dataIndex: 'passport_no', render: (v) => v || <Tag color="warning">Eksik</Tag> },
          {
            title: 'Geçerlilik',
            dataIndex: 'passport_expiry',
            width: 120,
            render: (v) => (v ? dayjs(v).format('DD.MM.YYYY') : <Tag color="warning">Eksik</Tag>),
          },
          {
            title: '',
            width: 70,
            render: (_, r) => (
              <Button
                size="small"
                danger
                onClick={async () => {
                  try {
                    await deleteParentTraveler(r.id);
                    message.success('Çıkarıldı.');
                    onChanged();
                  } catch (e) { message.error(apiErrorMessage(e)); }
                }}
              >
                Sil
              </Button>
            ),
          },
        ]}
      />

      {remaining > 0 && (
        <div style={{ marginTop: 12 }}>
          <Text strong>Kişi Ekle</Text>
          <Row gutter={[8, 8]} style={{ marginTop: 8 }}>
            <Col xs={24} sm={12}>
              <Input
                placeholder="Ad Soyad"
                value={form.full_name}
                onChange={(e) => setForm({ ...form, full_name: e.target.value })}
              />
            </Col>
            <Col xs={24} sm={12}>
              <select
                value={form.duty}
                onChange={(e) => setForm({ ...form, duty: e.target.value })}
                style={{ width: '100%', height: 32, borderRadius: 6, border: '1px solid #d9d9d9', padding: '0 8px' }}
              >
                <option value="student">Öğrenci</option>
                <option value="parent">Veli</option>
                <option value="other">Diğer</option>
              </select>
            </Col>
            <Col xs={24} sm={12}>
              <Input
                type="date"
                placeholder="Doğum Tarihi"
                value={form.birth_date}
                onChange={(e) => setForm({ ...form, birth_date: e.target.value })}
              />
            </Col>
            <Col xs={24} sm={12}>
              <Input
                placeholder="TC Kimlik No"
                maxLength={20}
                value={form.id_number}
                onChange={(e) => setForm({ ...form, id_number: e.target.value })}
              />
            </Col>
            <Col xs={24} sm={12}>
              <Input
                placeholder="Pasaport No"
                maxLength={30}
                value={form.passport_no}
                onChange={(e) => setForm({ ...form, passport_no: e.target.value })}
              />
            </Col>
            <Col xs={24} sm={12}>
              <Input
                type="date"
                placeholder="Pasaport Geçerlilik"
                value={form.passport_expiry}
                onChange={(e) => setForm({ ...form, passport_expiry: e.target.value })}
              />
            </Col>
          </Row>
          <Button type="primary" style={{ marginTop: 8 }} loading={saving} onClick={add}>
            Ekle
          </Button>
        </div>
      )}
    </Card>
  );
};

export default ParentCompetitions;
